// Entity groups — the ThingsBoard PE permission-aware view onto entities.
//
// `GET /api/tenant/assets` is TENANT_ADMIN-only; a CUSTOMER_USER gets a 403.
// But a tenant-owned asset SHARED with a customer via an asset GROUP (read
// permission) IS visible to that customer through the entity-group API:
//
//   GET /api/entityGroups/ASSET        → asset groups the user can read
//   GET /api/entityGroup/{id}/assets   → members, as plain PageData<Entity>
//
// This is the discovery path for market-data assets (electricity bidding
// zones), which live in a tenant asset group shared read-only with customers.
// It works uniformly for TENANT_ADMIN (sees every tenant group) and
// CUSTOMER_USER (sees their own groups + any shared group). The member
// endpoint returns the exact same entity shape as `/api/tenant/assets`, so
// consumers need no mapper. Verified against TB PE 4.3.1.
//
// Ported from the TB-EMS SPA so the operator-spa's Market price page
// discovers zones the same way and works for customer users.

import type { TbHttp } from "./http";
import type { PageData, TbEntity } from "./types";

/** Entity-group type — the subset this SDK uses. TB also has CUSTOMER, USER, … */
export type EntityGroupType = "ASSET" | "DEVICE";

export type EntityGroup = {
  id: { id: string; entityType: "ENTITY_GROUP" };
  name: string;
  /** The grouped entity type — 'ASSET', 'DEVICE', … */
  type: string;
  ownerId: { id: string; entityType: string };
  /** True for the auto-managed catch-all "All" group. */
  groupAll: boolean;
};

/**
 * List the entity groups of `groupType` the current user can read.
 * TENANT_ADMIN sees every tenant group; CUSTOMER_USER sees their own groups
 * plus any tenant group shared with them. Returns a plain array (not paged).
 *
 * Endpoint: GET /api/entityGroups/{groupType}
 */
export function listEntityGroups(
  http: TbHttp,
  groupType: EntityGroupType,
): Promise<EntityGroup[]> {
  return http.get(`/api/entityGroups/${groupType}`);
}

/**
 * List ASSET members of an entity group. Returns the same `PageData<TbEntity>`
 * shape as `/api/tenant/assets` — no mapper required at the call site.
 *
 * Endpoint: GET /api/entityGroup/{groupId}/assets
 */
export function listEntityGroupAssets(
  http: TbHttp,
  groupId: string,
  opts: { pageSize?: number; page?: number } = {},
): Promise<PageData<TbEntity>> {
  const p = new URLSearchParams({
    pageSize: String(opts.pageSize ?? 100),
    page: String(opts.page ?? 0),
  });
  return http.get<PageData<TbEntity>>(`/api/entityGroup/${groupId}/assets?${p}`);
}
